import type { SettlementTransaction } from '@/lib/types/settlement';
import type { User, Game } from '@/lib/types';
import type { PlayerDebt, PlayerDebtGame } from '@/lib/types/debts';

export function calculatePlayerDebts(
  transactions: SettlementTransaction[],
  users: User[],
  games: Game[]
): { playerDebts: PlayerDebt[]; gameMap: Map<string, PlayerDebtGame[]> } {
  const pending = transactions.filter(t => t.status === 'pending');

  const userMap = new Map(users.map(u => [u.id, u]));
  const gameMap = new Map(games.map(g => [g.id, g]));

  // Per-user totals: owed (as payer) and owing (as payee)
  const totals = new Map<string, { owed: number; owing: number }>();

  // Per-user per-game debt details
  const userGameDebts = new Map<string, Map<string, PlayerDebtGame>>();

  for (const tx of pending) {
    // Skip orphaned transactions whose game is not in the loaded games collection
    if (!gameMap.has(tx.game_id)) continue;

    const fromId = tx.from_user_id;
    const toId = tx.to_user_id;

    // Update totals
    for (const [uid, delta] of [[fromId, 'owed'] as const, [toId, 'owing'] as const]) {
      if (!totals.has(uid)) totals.set(uid, { owed: 0, owing: 0 });
      const t = totals.get(uid)!;
      if (delta === 'owed') t.owed += tx.amount;
      else t.owing += tx.amount;
    }

    // Track per-game debts for each participant
    for (const uid of [fromId, toId]) {
      if (!userGameDebts.has(uid)) userGameDebts.set(uid, new Map());
      const gameDebts = userGameDebts.get(uid)!;
      if (!gameDebts.has(tx.game_id)) {
        const game = gameMap.get(tx.game_id);
        gameDebts.set(tx.game_id, {
          gameId: tx.game_id,
          date: game?.date ?? '',
          venue: game?.venue ?? '',
          owes: [],
          owed: [],
        });
      }
      const entry = gameDebts.get(tx.game_id)!;
      if (uid === fromId) {
        entry.owes.push({
          toUserId: toId,
          toName: userMap.get(toId)?.name ?? 'Unknown',
          amount: tx.amount,
        });
      } else {
        entry.owed.push({
          fromUserId: fromId,
          fromName: userMap.get(fromId)?.name ?? 'Unknown',
          amount: tx.amount,
        });
      }
    }
  }

  // Build player list
  const playerDebts: PlayerDebt[] = [];
  for (const [userId, t] of totals) {
    const user = userMap.get(userId);
    playerDebts.push({
      userId,
      name: user?.name ?? 'Unknown',
      photoUrl: user?.custom_photo_url ?? user?.photo_url ?? null,
      totalOwed: t.owed,
      totalOwing: t.owing,
    });
  }

  // Default sort: owes most first
  playerDebts.sort((a, b) => b.totalOwed - a.totalOwed);

  // Convert game map
  const resultGameMap = new Map<string, PlayerDebtGame[]>();
  for (const [userId, gamesMap] of userGameDebts) {
    resultGameMap.set(userId, Array.from(gamesMap.values()));
  }

  return { playerDebts, gameMap: resultGameMap };
}
